import { create } from "zustand";
import { accessService } from "../services/access";
import type { AccessLayers } from "../services/access";

export type AccessStatus = "idle" | "loading" | "ready" | "failed";

const RETRIES = 2;
const RETRY_DELAY_MS = 800;

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

interface AccessState {
  userId: string | null;
  layers: AccessLayers | null;
  status: AccessStatus;
  load: (userId: string) => Promise<void>;
  /** Fija los accesos ya conocidos (los que trae /login): sin pedir /access/me. */
  hydrate: (userId: string, layers: AccessLayers) => void;
  clear: () => void;
}


export const useAccessStore = create<AccessState>((set, get) => ({
  userId: null,
  layers: null,
  status: "idle",

  load: async (userId) => {
    const state = get();
    if (state.status === "loading" && state.userId === userId) return;
    if (state.status === "ready" && state.userId === userId) return;

    set({ userId, status: "loading" });

    for (let intento = 0; intento <= RETRIES; intento += 1) {
      try {
        const layers = await accessService.me();
        if (get().userId !== userId) return;
        set({ layers, status: "ready" });
        return;
      } catch (error) {
        if (get().userId !== userId) return;
        if (intento === RETRIES) {
          console.error("No se pudieron cargar los accesos del usuario:", error);
          set({ layers: null, status: "failed" });
          return;
        }
        await wait(RETRY_DELAY_MS * (intento + 1));
      }
    }
  },

  hydrate: (userId, layers) => set({ userId, layers, status: "ready" }),

  clear: () => set({ userId: null, layers: null, status: "idle" }),
}));

export default useAccessStore;
